import { cn } from "@ronin/ui";
import { Link } from "@tanstack/react-router";
import {
  CalendarDays,
  House,
  type LucideIcon,
  NotebookPen,
  Settings,
  SquareCheckBig,
} from "lucide-react";
import type { ReactNode } from "react";

const itemClass = cn(
  "relative flex items-center justify-center text-dim-foreground transition-colors duration-150",
  "focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring",
  "data-[status=active]:text-brand data-[status=active]:after:absolute data-[status=active]:after:bottom-0 data-[status=active]:after:h-0.5 data-[status=active]:after:bg-ring",
  // Phones: a tab of the bar, marker along the bottom edge.
  "h-14 flex-1 data-[status=active]:after:inset-x-6",
  // From md: a square dock item, tinted when active, marker along the bottom edge.
  "md:size-10 md:flex-none md:pointer-coarse:size-11 md:hover:bg-muted md:hover:text-secondary-foreground",
  "md:data-[status=active]:bg-muted md:data-[status=active]:after:inset-x-2",
);

function Item({
  to,
  label,
  children,
}: {
  to: "/" | "/todos" | "/calendar" | "/notes" | "/settings";
  label: string;
  children: ReactNode;
}) {
  return (
    <Link
      to={to}
      // Home ("/") is active only on the dashboard itself, whichever board is open.
      activeOptions={{ exact: to === "/", includeSearch: false }}
      aria-label={label}
      title={label}
      className={itemClass}
    >
      {children}
    </Link>
  );
}

const sections: Array<{
  to: "/" | "/todos" | "/calendar" | "/notes";
  label: string;
  icon: LucideIcon;
}> = [
  { to: "/", label: "Home", icon: House },
  { to: "/todos", label: "Todos", icon: SquareCheckBig },
  { to: "/calendar", label: "Calendar", icon: CalendarDays },
  { to: "/notes", label: "Notes", icon: NotebookPen },
];

/**
 * Bottom navigation (DESIGN.md), icons only: on phones a full-width tab bar, from md a
 * floating dock. App sections first, then settings.
 */
export function BottomNav() {
  return (
    <nav
      aria-label="Main"
      className={cn(
        "fixed inset-x-0 bottom-0 z-40 flex border-t bg-card/95 pb-[env(safe-area-inset-bottom)] backdrop-blur-lg",
        "md:inset-x-auto md:bottom-[calc(1rem+env(safe-area-inset-bottom))] md:left-1/2 md:-translate-x-1/2 md:items-center md:gap-1 md:border md:bg-card/90 md:p-1.5 md:shadow-bar",
      )}
    >
      {sections.map(({ to, label, icon: Icon }) => (
        <Item key={to} to={to} label={label}>
          <Icon className="size-5 md:size-[18px]" aria-hidden />
        </Item>
      ))}
      <span className="mx-1 hidden h-6 w-px bg-border md:block" aria-hidden />
      <Item to="/settings" label="Settings">
        <Settings className="size-5 md:size-[18px]" aria-hidden />
      </Item>
    </nav>
  );
}
